
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js';

import { COHORT_COLORS } from './AgingChart.vue';

export interface TrendCohort {
  key: string;
  label: string;
}

export interface TrendPoint {
  date: string;
  overdueTotal: number;
  openTotal: number;
  [cohortKey: string]: string | number;
}

const eur = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * The aging position over time, as stacked cohorts per snapshot day.
 *
 * Points are plotted against a category axis of the days that actually have a
 * snapshot — deliberately not a continuous date axis. The sync writes one
 * snapshot per day, so a gap means it did not run, and stretching the line
 * across that gap would draw a position nobody ever measured.
 */
@Component
export default class S4cAgingTrend extends Vue {
  @Prop({ required: true }) points!: TrendPoint[];
  @Prop({ required: true }) cohorts!: TrendCohort[];

  private chart: Chart | null = null;

  mounted() {
    this.draw();
  }

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  @Watch('points')
  @Watch('cohorts')
  onDataChange() {
    this.draw();
  }

  private get labels(): string[] {
    return this.points.map((p) => p.date);
  }

  private get datasets(): Chart.ChartDataSets[] {
    return this.cohorts.map((c) => ({
      label: c.label,
      backgroundColor: COHORT_COLORS[c.key] || '#90a4ae',
      data: this.points.map((p) => Number(p[c.key]) || 0),
    }));
  }

  private draw() {
    if (!this.$refs.canvas) {
      return;
    }
    const ctx = (this.$refs.canvas as HTMLCanvasElement).getContext('2d');
    if (!ctx) {
      return;
    }

    const data = { labels: this.labels, datasets: this.datasets };

    if (this.chart) {
      this.chart.data.labels = data.labels;
      this.chart.data.datasets = data.datasets;
      this.chart.update();
      return;
    }

    this.chart = new Chart(ctx, {
      type: 'bar',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'top' },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, chartData) => {
              const ds = chartData.datasets?.[item.datasetIndex ?? 0];
              return `${ds?.label ?? ''}: ${eur.format(
                Number(item.yLabel) || 0,
              )}`;
            },
            footer: (items) =>
              `${this.$t('accounting.s4c-aging.open-total')}: ${eur.format(
                items.reduce((s, i) => s + (Number(i.yLabel) || 0), 0),
              )}`,
          },
        },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [
            {
              stacked: true,
              ticks: { callback: (value) => eur.format(Number(value)) },
            },
          ],
        },
      },
    });
  }
}
